import React, { useEffect, useRef, useState } from 'react';
import DsButton from '../../common/DsButton';
import presets from './groupChatIconPresets.json';
import colors from './groupChatIconColors.json';
import '../../../css/admin/chats/GroupChatIconEditor.css';
export type GroupChatIconChoice = { presetId: string; colorId: string; assetId: string; iconFile: string };
export const groupChatIconChoice = (presetId = 'note', colorId = ''): GroupChatIconChoice => {
  const preset = presets.find((item) => item.id === presetId);
  const assetId = colorId ? preset?.variants.find((item) => item.colorId === colorId)?.assetId : preset?.id;
  if (!assetId) throw new Error('アイコンを選び直してください。');
  return { presetId, colorId, assetId, iconFile: `preset-${assetId}.png` };
};
export const groupChatIconChoiceFromFile = (file?: string | null): GroupChatIconChoice | null => {
  for (const preset of presets) {
    const variant = preset.variants.find((item) => `preset-${item.assetId}.png` === file);
    if (variant) return groupChatIconChoice(preset.id, variant.assetId === preset.id ? '' : variant.colorId);
  }
  return null;
};
export default function GroupChatIconPresetPicker({ disabled, initialChoice, onApply }: { disabled: boolean; initialChoice?: GroupChatIconChoice | null; onApply: (choice: GroupChatIconChoice) => Promise<boolean> }) {
  const [selected, setSelected] = useState(initialChoice?.presetId || 'note');
  const [colorId, setColorId] = useState(initialChoice?.colorId || '');
  const [category, setCategory] = useState(presets.find((item) => item.id === selected)?.category || 'music');
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState('');
  const active = useRef(true), lock = useRef(false);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const busy = disabled || preparing;
  const assetId = (preset: typeof presets[number]) => groupChatIconChoice(preset.id, colorId).assetId;
  const apply = async () => {
    if (busy || lock.current) return;
    lock.current = true; setPreparing(true); setError('');
    try { await onApply(groupChatIconChoice(selected, colorId)); }
    catch (failure) { if (active.current) setError(failure instanceof Error ? failure.message : 'アイコンを更新できませんでした。'); }
    finally { lock.current = false; if (active.current) setPreparing(false); }
  };
  return <div className="group-icon-editor">
    <img className="group-icon-editor__choicePreview" src={`/group-chat-icons/${groupChatIconChoice(selected, colorId).assetId}.png`} alt="選択中のアイコン" width="56" height="56" />
      <div className="group-icon-editor__categories" role="group" aria-label="アイコンの種類">
        {[['music', '音楽'], ['symbol', 'シンボル']].map(([id, label]) => (
          <DsButton key={id} variant="secondary" appearance="text" aria-pressed={category === id} disabled={busy} onClick={() => setCategory(id)}>{label}</DsButton>
        ))}
      </div>
      <div className="group-icon-editor__grid" role="group" aria-label={category === 'music' ? '音楽アイコン' : 'シンボルアイコン'}>
        {presets.filter((preset) => preset.category === category).map((preset) => (
          <DsButton key={preset.id} variant="secondary" appearance="text" className="group-icon-editor__option" aria-pressed={selected === preset.id}
            aria-label={preset.label} title={preset.label}
            disabled={busy} onClick={() => { setSelected(preset.id); setError(''); }}>
            <img src={`/group-chat-icons/${assetId(preset)}.png`} alt="" width="44" height="44" />
          </DsButton>
        ))}
      </div>
      <div className="group-icon-editor__colors" role="group" aria-label="カラー">
        {colors.map((color) => (
          <DsButton key={color.id} variant="secondary" appearance="text" className="group-icon-editor__color" aria-pressed={colorId === color.id}
            aria-label={color.label} title={color.label}
            disabled={busy} onClick={() => { setColorId(color.id); setError(''); }}>
            <img src={`/group-chat-icons/${color.assetId}.png`} alt="" width="28" height="28" />
          </DsButton>
        ))}
      </div>
    <DsButton variant="modify" aria-label="このアイコンを使う" disabled={busy} loading={preparing} onClick={apply}>適用</DsButton>
    {error && <p role="alert" className="group-icon-editor__error">{error}</p>}
  </div>;
}
